import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Edit, Loader2, CheckCircle2 } from 'lucide-react';

/**
 * EditUserDialog
 * --------------
 * Pure-presentational user-edit modal extracted from the Admin
 * Dashboard. Owns the form layout (Name, Email-locked, Role,
 * Organization, Password Reset, Notification toggles) but delegates
 * all state to the parent — the parent stays the single source of
 * truth for `editingUser`, `editForm`, and the save handler.
 *
 * Props:
 *   editingUser           — the user being edited (null = closed)
 *   editForm              — { name, role, organization_id, newPassword, notifications }
 *   setEditForm           — updater (functional form)
 *   onClose               — close handler (sets editingUser back to null)
 *   onSave                — submit handler (calls the API)
 *   editSaving            — disables the Save button while in-flight
 *   organizations         — for the Organization dropdown
 *   currentUserIsSuperAdmin — gates the "super_admin" role option
 */
const EditUserDialog = ({
  editingUser,
  editForm,
  setEditForm,
  onClose,
  onSave,
  editSaving,
  organizations,
  currentUserIsSuperAdmin,
}) => (
  <Dialog open={!!editingUser} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Edit size={18} className="text-teal-600" />
          Edit User
        </DialogTitle>
        <DialogDescription>
          Update details for {editingUser?.email}
        </DialogDescription>
      </DialogHeader>
      {editingUser && (
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {/* Name */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Full Name</Label>
            <Input
              value={editForm.name}
              onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
              className="mt-1"
              data-testid="edit-user-name"
            />
          </div>

          {/* Email (locked) */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Email</Label>
            <Input
              value={editingUser.email}
              disabled
              className="mt-1 bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-1">Email is locked to the user account</p>
          </div>

          {/* Role */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Role</Label>
            <Select value={editForm.role} onValueChange={(v) => setEditForm(f => ({ ...f, role: v }))}>
              <SelectTrigger className="mt-1" data-testid="edit-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currentUserIsSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                <SelectItem value="org_admin">Org Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Organization */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Organization</Label>
            <Select value={editForm.organization_id} onValueChange={(v) => setEditForm(f => ({ ...f, organization_id: v }))}>
              <SelectTrigger className="mt-1" data-testid="edit-user-org">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-400 mt-1">Reassigning moves the user and their role to the new org</p>
          </div>

          {/* Password Reset */}
          <div className="pt-3 border-t">
            <Label className="text-sm font-medium text-gray-700">Reset Password</Label>
            {editingUser.auth_provider === 'google' ? (
              <p className="text-xs text-amber-600 mt-1 p-2 bg-amber-50 rounded">
                This user authenticates via Google OAuth. Password reset is not available.
              </p>
            ) : (
              <>
                <Input
                  type="password"
                  placeholder="Enter new password (leave blank to keep current)"
                  value={editForm.newPassword}
                  onChange={(e) => setEditForm(f => ({ ...f, newPassword: e.target.value }))}
                  className="mt-1"
                  data-testid="edit-user-password"
                />
                <p className="text-xs text-gray-400 mt-1">Leave blank to keep the current password</p>
              </>
            )}
          </div>

          {/* Notifications */}
          <div className="pt-3 border-t">
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Notification Preferences</Label>
            <div className="space-y-2">
              <NotifToggle
                label="Email Notifications"
                hint="Alerts and report emails"
                checked={editForm.notifications?.email !== false}
                onChange={(c) => setEditForm(f => ({ ...f, notifications: { ...f.notifications, email: c } }))}
                testId="edit-user-notif-email"
              />
              <NotifToggle
                label="LEI Alert Notifications"
                hint="LEI score threshold alerts"
                checked={editForm.notifications?.lei_alerts !== false}
                onChange={(c) => setEditForm(f => ({ ...f, notifications: { ...f.notifications, lei_alerts: c } }))}
                testId="edit-user-notif-lei"
              />
              <NotifToggle
                label="Weekly Summary Reports"
                hint="Receive weekly digest"
                checked={editForm.notifications?.weekly_reports || false}
                onChange={(c) => setEditForm(f => ({ ...f, notifications: { ...f.notifications, weekly_reports: c } }))}
                testId="edit-user-notif-weekly"
              />
            </div>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} disabled={editSaving} data-testid="save-edit-user-btn">
          {editSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle2 size={14} className="mr-2" />}
          Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const NotifToggle = ({ label, hint, checked, onChange, testId }) => (
  <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
    <div>
      <p className="text-sm text-gray-700">{label}</p>
      <p className="text-xs text-gray-400">{hint}</p>
    </div>
    <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export default EditUserDialog;
